import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMail, FiSend, FiPaperclip, FiX, FiCheck } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService } from '../../services';

const ClienteMensajes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [mensajesEnviados, setMensajesEnviados] = useState([]);
  const [formData, setFormData] = useState({
    destinatario: '',
    asunto: '',
    mensaje: '',
    copias: '',
    plantilla: 'personalizado'
  });
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);

  useEffect(() => {
    loadClienteData();
    loadMensajesEnviados();
  }, [id]);

  const loadClienteData = async () => {
    setIsLoading(true);
    try {
      const result = await clientesService.getCliente(id);
      if (result.success) {
        setCliente(result.data);
        setFormData(prev => ({
          ...prev,
          destinatario: result.data.email || ''
        }));
      }
    } catch (error) {
      console.error('Error cargando cliente:', error);
      setError('Error al cargar información del cliente');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMensajesEnviados = async () => {
    try {
      const result = await clientesService.getEmails(id);
      
      if (result.success) {
        const mensajesFormateados = result.data.map(email => ({
          id: email.id,
          asunto: email.asunto,
          fecha: email.created_at,
          estado: email.estado.toLowerCase(),
          destinatario: email.destinatario
        }));
        setMensajesEnviados(mensajesFormateados);
      }
    } catch (error) {
      console.error('Error cargando mensajes:', error);
    }
  };

  const plantillasEmail = {
    personalizado: { nombre: 'Mensaje Personalizado', asunto: '', contenido: '' },
    seguimiento: {
      nombre: 'Seguimiento de Pedido',
      asunto: 'Seguimiento de su pedido',
      contenido: 'Estimado cliente,\n\nNos comunicamos para informarle sobre el estado de su pedido.\n\nSaludos cordiales,\nEquipo Betel Natural'
    },
    catalogo: {
      nombre: 'Envío de Catálogo',
      asunto: 'Catálogo de Productos',
      contenido: 'Estimado cliente,\n\nAdjuntamos nuestro catálogo actualizado de productos para su revisión.\n\nSaludos cordiales,\nEquipo Betel Natural'
    },
    promocion: {
      nombre: 'Promoción Especial',
      asunto: 'Promoción Especial para Usted',
      contenido: 'Estimado cliente,\n\nTenemos promociones especiales disponibles para usted.\n\nSaludos cordiales,\nEquipo Betel Natural'
    },
    recordatorio: {
      nombre: 'Recordatorio de Pago',
      asunto: 'Recordatorio - Facturas Pendientes',
      contenido: 'Estimado cliente,\n\nLe recordamos que tiene facturas pendientes de pago.\n\nSaludos cordiales,\nEquipo Betel Natural'
    }
  };

  const handlePlantillaChange = (plantilla) => {
    const plantillaSeleccionada = plantillasEmail[plantilla];
    setFormData(prev => ({
      ...prev,
      plantilla,
      asunto: plantillaSeleccionada.asunto,
      mensaje: plantillaSeleccionada.contenido
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setArchivosAdjuntos(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setArchivosAdjuntos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setError(null);

    try {
      const emailData = {
        destinatario: formData.destinatario,
        asunto: formData.asunto,
        mensaje: formData.mensaje,
        copias: formData.copias,
        // El backend valida el enum por su VALOR en minúscula (personalizado, seguimiento, ...)
        template: formData.plantilla,
        // Los archivos adjuntos requerirían FormData, por ahora los omitimos
      };

      const result = await clientesService.enviarEmail(id, emailData);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 5000);

        // Recargar historial
        await loadMensajesEnviados();

        // Resetear formulario
        setFormData({
          destinatario: cliente?.email || '',
          asunto: '',
          mensaje: '',
          copias: '',
          plantilla: 'personalizado'
        });
        setArchivosAdjuntos([]);
      } else {
        setError(result.message || 'Error al enviar el correo');
      }
    } catch (error) {
      console.error('Error enviando email:', error);
      setError('Error al enviar el correo electrónico');
    } finally {
      setIsSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando información..." />;
  }

  return (
    <div className="cliente-mensajes-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">ENVIAR MENSAJES</h1>
        <div style={{ width: '140px' }} />
      </div>

      {/* Error/Success messages */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert--success" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FiCheck size={20} />
          <span>Correo enviado exitosamente</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
        {/* Formulario de Envío */}
        <div className="email-form-card" style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiMail size={24} />
            Nuevo Correo Electrónico
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Plantilla</label>
              <select
                className="form-input"
                value={formData.plantilla}
                onChange={(e) => handlePlantillaChange(e.target.value)}
              >
                {Object.entries(plantillasEmail).map(([key, plantilla]) => (
                  <option key={key} value={key}>
                    {plantilla.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Para (Destinatario) *</label>
              <input
                type="email"
                className="form-input"
                value={formData.destinatario}
                onChange={(e) => setFormData({ ...formData, destinatario: e.target.value })}
                placeholder="correo@ejemplo.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">CC (Copias)</label>
              <input
                type="text"
                className="form-input"
                value={formData.copias}
                onChange={(e) => setFormData({ ...formData, copias: e.target.value })}
                placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
              />
              <small style={{ color: '#6c757d', fontSize: '12px' }}>
                Separar múltiples correos con comas
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Asunto *</label>
              <input
                type="text"
                className="form-input"
                value={formData.asunto}
                onChange={(e) => setFormData({ ...formData, asunto: e.target.value })}
                placeholder="Asunto del correo"
                required
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Mensaje *</label>
              <textarea
                className="form-input"
                value={formData.mensaje}
                onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                placeholder="Escribe aquí tu mensaje..."
                rows={10}
                required
                maxLength={5000}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Archivos Adjuntos</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label htmlFor="file-upload" className="btn btn--secondary" style={{ cursor: 'pointer', margin: 0 }}>
                  <FiPaperclip /> Adjuntar Archivos
                </label>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <small style={{ color: '#6c757d' }}>
                  {archivosAdjuntos.length > 0 && `${archivosAdjuntos.length} archivo(s) seleccionado(s)`}
                </small>
              </div>

              {archivosAdjuntos.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  {archivosAdjuntos.map((file, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      marginBottom: '5px'
                    }}>
                      <span style={{ fontSize: '14px' }}>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer'
                        }}
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="submit" className="btn btn--primary" disabled={isSending}>
                {isSending ? (
                  <>
                    <span className="spinner-small"></span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <FiSend /> Enviar Correo
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Historial de Mensajes Enviados */}
        <div className="email-history-card" style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxHeight: '600px',
          overflow: 'auto'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#333' }}>
            Historial de Mensajes
          </h3>

          {mensajesEnviados.length === 0 ? (
            <p style={{ color: '#6c757d', textAlign: 'center', padding: '20px' }}>
              No hay mensajes enviados
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {mensajesEnviados.map((mensaje) => (
                <div key={mensaje.id} style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  borderLeft: '3px solid #7B2D8E'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '5px' }}>
                    {mensaje.asunto}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                    {formatDate(mensaje.fecha)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '5px' }}>
                    Para: {mensaje.destinatario}
                  </div>
                  <span style={{
                    display: 'inline-block',
                    marginTop: '5px',
                    padding: '2px 8px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    {mensaje.estado}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute={`/system/clientes/${id}`}
        currentTab="mensajes"
      />
    </div>
  );
};

export default ClienteMensajes;
